/**
 * C 端「自选持仓」接口；Mock 规则见前端规范。
 */
import { post } from "../../utils/Backend";
import { useLongfansMock } from "../../global/GlobalMock";
import type {
  AppUserHoldingsListResultDTO,
  AppUserHoldingLineDTO,
  ClosedTradeListResultDTO,
  MonthlyReviewDetailDTO,
  MonthlyReviewListDTO,
  OpenHoldingLookupResultDTO,
  ProfitCurveResultDTO,
  RefreshDailyProfitStatResultDTO,
  UserHoldingsHistoryResultDTO,
} from "./UserHoldingsType";
import { userHoldingsMocks } from "./UserHoldingsMock";

/** 持仓走真实库与接口；恢复本地 Mock 时改为 `useLongfansMock` */
const mockUserHoldings = useLongfansMock && false;

const BASE = "/lark/app/userHoldings";

async function guard<T>(
  mock: () => T | Promise<T>,
  real: () => Promise<T | string>
): Promise<T | null> {
  if (mockUserHoldings) {
    return (await mock()) as T;
  }
  const r = await real();
  return typeof r === "string" ? null : (r as T);
}

export const UserHoldingsApi = {
  async listHoldings(spaceId: string): Promise<AppUserHoldingsListResultDTO | null> {
    return guard(
      () => ({ rows: userHoldingsMocks.listAll() }),
      () =>
        post<Record<string, unknown>, AppUserHoldingsListResultDTO>(
          `${BASE}/listHoldings`,
          { spaceId },
          { silent: true }
        )
    );
  },

  async openBuy(
    spaceId: string,
    param: {
      stockCode: string;
      stockName?: string;
      buyPrice: number;
      buyShares: number;
      buyTime: string;
      reasonType: string;
      reasonDetail: string;
    }
  ): Promise<boolean> {
    if (mockUserHoldings) {
      return userHoldingsMocks.openBuy({
        stockCode: param.stockCode,
        buyPrice: param.buyPrice,
        buyShares: param.buyShares,
        buyTime: param.buyTime,
        reasonType: param.reasonType,
        reasonDetail: param.reasonDetail,
      });
    }
    const ok = await post(
      `${BASE}/openBuy`,
      { spaceId, ...param },
      { silent: true }
    );
    return typeof ok !== "string";
  },

  async findOpenHoldingByStock(
    spaceId: string,
    stockCode: string
  ): Promise<AppUserHoldingLineDTO | null> {
    if (mockUserHoldings) {
      const all = userHoldingsMocks.listAll();
      const code = stockCode.trim().toUpperCase();
      const row = all.find(
        (r) =>
          String(r.stockCode ?? "")
            .trim()
            .toUpperCase() === code && r.holdingStatus === "OPEN"
      );
      return row ?? null;
    }
    const r = await post<{ spaceId: string; stockCode: string }, OpenHoldingLookupResultDTO>(
      `${BASE}/findOpenHoldingByStock`,
      { spaceId, stockCode },
      { silent: true }
    );
    if (typeof r === "string" || r == null) {
      return null;
    }
    return r.openHolding ?? null;
  },

  /** silent：失败时返回错误文案，成功返回 null */
  async openBuyTry(
    spaceId: string,
    param: {
      stockCode: string;
      stockName?: string;
      buyPrice: number;
      buyShares: number;
      buyTime: string;
      reasonType: string;
      reasonDetail: string;
    }
  ): Promise<string | null> {
    if (mockUserHoldings) {
      const ok = userHoldingsMocks.openBuy({
        stockCode: param.stockCode,
        buyPrice: param.buyPrice,
        buyShares: param.buyShares,
        buyTime: param.buyTime,
        reasonType: param.reasonType,
        reasonDetail: param.reasonDetail,
      });
      return ok ? null : "建仓失败";
    }
    const res = await post(
      `${BASE}/openBuy`,
      { spaceId, ...param },
      { silent: true }
    );
    return typeof res === "string" ? res : null;
  },

  async addBuy(
    spaceId: string,
    param: Record<string, unknown>
  ): Promise<boolean> {
    if (mockUserHoldings) {
      return userHoldingsMocks.addBuy();
    }
    const ok = await post(`${BASE}/addBuy`, { spaceId, ...param }, { silent: true });
    return typeof ok !== "string";
  },

  async sell(
    spaceId: string,
    param: Record<string, unknown>
  ): Promise<boolean> {
    if (mockUserHoldings) {
      return userHoldingsMocks.sell();
    }
    const ok = await post(`${BASE}/sell`, { spaceId, ...param }, { silent: true });
    return typeof ok !== "string";
  },

  async listProfitCurve(spaceId: string): Promise<ProfitCurveResultDTO | null> {
    return guard(
      () => ({ points: userHoldingsMocks.curvePoints }),
      () =>
        post(`${BASE}/listProfitCurve`, { spaceId }, { silent: true }) as Promise<
          ProfitCurveResultDTO | string
        >
    );
  },

  async listClosedTrades(spaceId: string): Promise<ClosedTradeListResultDTO | null> {
    return guard(
      () => ({ rows: userHoldingsMocks.closedTradeRows }),
      () =>
        post(`${BASE}/listClosedTrades`, { spaceId }, { silent: true }) as Promise<
          ClosedTradeListResultDTO | string
        >
    );
  },

  async listTradeHistory(spaceId: string): Promise<UserHoldingsHistoryResultDTO | null> {
    return guard(
      () => ({ rows: userHoldingsMocks.historyRows }),
      () =>
        post(`${BASE}/listTradeHistory`, { spaceId }, { silent: true }) as Promise<
          UserHoldingsHistoryResultDTO | string
        >
    );
  },

  async refreshStat(spaceId: string): Promise<string | null> {
    if (mockUserHoldings) {
      return userHoldingsMocks.refresh();
    }
    const r = await post(
      `${BASE}/refreshDailyProfitStat`,
      { spaceId },
      { silent: true }
    );
    if (typeof r === "string") {
      return null;
    }
    return (r as RefreshDailyProfitStatResultDTO).statDateApplied ?? null;
  },

  async latestReview(spaceId: string): Promise<MonthlyReviewDetailDTO | null> {
    return guard(
      () => userHoldingsMocks.latestReview(),
      () =>
        post(`${BASE}/monthlyReview/latest`, { spaceId }, { silent: true }) as Promise<
          MonthlyReviewDetailDTO | string
        >
    );
  },

  async listReviews(spaceId: string): Promise<MonthlyReviewListDTO | null> {
    return guard(
      () => userHoldingsMocks.listReviews(),
      () =>
        post(`${BASE}/monthlyReview/list`, { spaceId }, { silent: true }) as Promise<
          MonthlyReviewListDTO | string
        >
    );
  },

  async reviewDetail(
    spaceId: string,
    reviewId: string
  ): Promise<MonthlyReviewDetailDTO | null> {
    return guard(
      () => userHoldingsMocks.detail(reviewId),
      () =>
        post(`${BASE}/monthlyReview/detail`, { spaceId, reviewId }, {
          silent: true,
        }) as Promise<MonthlyReviewDetailDTO | string>
    );
  },
};
